import React, { lazy } from 'react';

import { Navigate, redirect, type RouteObject } from 'react-router-dom';

import { ProtectedRoute } from '@packages/components/protected-route/protected-route';

import { DefaultDateRangeGuard } from './components/default-date-range-guard/default-date-range-guard';
import { HandleLegacyFiltersGuard } from './components/handle-legacy-filters-guard/handle-legacy-filters-guard';
import FlowDataPage from './flow-data/pages/flow-data/flow-data.page';

const RawLogSearchHistoryPage = lazy(
  () => import('./raw-log/pages/search-history/raw-log-search-history.page'),
);

const RawLogSearchSourcesPage = lazy(
  () => import('./raw-log/pages/search-sources/raw-log-search-sources.page'),
);

const RawLogSearchPage = lazy(
  () => import('./raw-log/pages/search/raw-log-search.page'),
);

const RawLogDownLoadPage = lazy(
  () => import('./raw-log/pages/download/download.page'),
);

const EventLogSearchPage = lazy(
  () => import('./event-log/pages/event-log-search/event-log-search.page'),
);

const EventLogSearchV2Page = lazy(
  () =>
    import('./event-log/pages/event-log-search-v2/event-log-search-v2.page'),
);

const SavedQueriesPage = lazy(
  () => import('./pages/saved-queries/saved-queries.page'),
);

const dataExplorerImprovedSearchFlag = 'DATA_EXPLORER_IMPROVED_SEARCH';

const eventlogV2RedirectLoader = () => {
  const improvedSearch =
    window.localStorage.getItem(dataExplorerImprovedSearchFlag) === 'true';

  const urlSearchParams = new URLSearchParams(window.location.search);

  if (improvedSearch) {
    throw redirect(`/data-explorer/event-log/search/v2?${urlSearchParams}`);
  }
  return null;
};

const featureFlagGuardLoader = () => {
  const improvedSearch =
    window.localStorage.getItem(dataExplorerImprovedSearchFlag) === 'true';

  if (!improvedSearch) {
    throw redirect('/data-explorer/event-log/search');
  }
  return null;
};

export const dataExplorerRoutes: RouteObject = {
  path: '/data-explorer/*',
  children: [
    {
      path: 'raw-log/*',
      element: (
        <ProtectedRoute legacyCapabilities={{ canAccessRawData: true }} />
      ),
      children: [
        {
          path: 'history',
          element: <RawLogSearchHistoryPage />,
        },
        {
          path: 'sources',
          element: <RawLogSearchSourcesPage />,
        },
        {
          path: 'search',
          element: <RawLogSearchPage />,
        },
        {
          path: 'search/:searchId',
          element: <RawLogSearchPage />,
        },
        {
          path: 'download/:queryId/:outputId/:fileId',
          element: <RawLogDownLoadPage />,
        },
      ],
    },
    {
      path: 'event-log/search/*',
      element: (
        <ProtectedRoute
          legacyCapabilities={{
            canAccessAnalyzedData: true,
            canAccessAnalyzedDataLite: true,
          }}
        />
      ),
      children: [
        {
          index: true,
          loader: eventlogV2RedirectLoader,
          element: <EventLogSearchPage />,
        },
        {
          path: 'v2',
          loader: featureFlagGuardLoader,
          element: <DefaultDateRangeGuard />,
          children: [
            {
              element: <HandleLegacyFiltersGuard />,
              children: [{ index: true, element: <EventLogSearchV2Page /> }],
            },
          ],
        },
        {
          path: 'queries',
          element: <SavedQueriesPage />,
        },
      ],
    },
    {
      path: 'flow/*',
      element: (
        <ProtectedRoute legacyCapabilities={{ canAccessDataFlow: true }} />
      ),
      children: [
        { index: true, element: <Navigate to="./device" replace /> },
        { path: ':flowDataType?', element: <FlowDataPage /> },
      ],
    },
  ],
};
